import { useCallback, useEffect, useRef, useState } from "react";
import { QUICK_RANGES, REFRESH_INTERVALS } from "../helpers/enums";
import { QuickRange, RefreshInterval, TimeRangePickerProps } from "../helpers/types";
import { sub, toInputValue } from "../helpers/date";

const ClockIcon = () => (
  <svg className="trp-trigger-icon" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const CaretIcon = () => (
  <svg className="trp-caret" viewBox="0 0 10 6" fill="currentColor">
    <path d="M0 0l5 6 5-6z"/>
  </svg>
);

const RefreshIcon = ({ spinning }: { spinning: boolean }) => (
  <svg
    className={`trp-refresh-icon${spinning ? " spin" : ""}`}
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);

const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
  onChange,
  defaultRangeKey = "last-6h",
}) => {
  const defaultQuick = QUICK_RANGES.find((r) => r.key === defaultRangeKey) ?? QUICK_RANGES[5];

  const [rangeOpen,   setRangeOpen]   = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);

  const [selectedQuick, setSelectedQuick] = useState<QuickRange | null>(defaultQuick);
  const [absFrom, setAbsFrom] = useState(toInputValue(sub(new Date(), 6 * 3_600_000)));
  const [absTo,   setAbsTo]   = useState(toInputValue(new Date()));
  const [absError, setAbsError] = useState("");

  const [refreshInterval, setRefreshInterval] = useState<RefreshInterval>(REFRESH_INTERVALS[0]);
  const [spinning,  setSpinning]  = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const rangeRef   = useRef<HTMLDivElement>(null);
  const refreshRef = useRef<HTMLDivElement>(null);
  const timerRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const cdownRef     = useRef<ReturnType<typeof setInterval> | null>(null);

  const emit = useCallback(
    (quick: QuickRange | null, fromStr?: string, toStr?: string) => {
      if (quick)               onChange({ from: quick.from(), to: quick.to() });
      else if (fromStr && toStr) onChange({ from: new Date(fromStr), to: new Date(toStr) });
    },
    [onChange]
  );

  // Initial
  useEffect(() => { emit(defaultQuick); /* eslint-disable-next-line */ }, []);

  const clearTimers = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (cdownRef.current) clearInterval(cdownRef.current);
    timerRef.current = cdownRef.current = null;
    setCountdown(null);
  };

  const startTimers = useCallback(
    (ms: number, quick: QuickRange | null, fromStr: string, toStr: string) => {
      clearTimers();
      setCountdown(Math.round(ms / 1000));
      cdownRef.current = setInterval(() => {
        setCountdown((c) => (c !== null && c > 1 ? c - 1 : Math.round(ms / 1000)));
      }, 1000);
      timerRef.current = setInterval(() => {
        setSpinning(true);
        setTimeout(() => setSpinning(false), 700);
        emit(quick, fromStr, toStr);
      }, ms);
    },
    [emit]
  );

  useEffect(() => {
    clearTimers();
    if (refreshInterval.ms)
      startTimers(refreshInterval.ms, selectedQuick, absFrom, absTo);
    return clearTimers;
    /* eslint-disable-next-line */
  }, [refreshInterval]);

  // Outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (rangeRef.current   && !rangeRef.current.contains(e.target as Node))   setRangeOpen(false);
      if (refreshRef.current && !refreshRef.current.contains(e.target as Node)) setRefreshOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const applyQuick = (r: QuickRange) => {
    setSelectedQuick(r);
    emit(r);
    setRangeOpen(false);
    if (refreshInterval.ms) startTimers(refreshInterval.ms, r, absFrom, absTo);
  };

  const applyAbsolute = () => {
    const from = new Date(absFrom);
    const to   = new Date(absTo);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) { setAbsError("Invalid date format."); return; }
    if (from >= to) { setAbsError("'From' must be before 'To'."); return; }
    setAbsError("");
    setSelectedQuick(null);
    onChange({ from, to });
    setRangeOpen(false);
  };

  const manualRefresh = () => {
    setSpinning(true);
    setTimeout(() => setSpinning(false), 700);
    emit(selectedQuick, absFrom, absTo);
  };

  const triggerLabel = selectedQuick
    ? selectedQuick.label
    : `${absFrom.replace("T", " ")} → ${absTo.replace("T", " ")}`;

  const isRunning = !!refreshInterval.ms;

  return (
    <div className="trp-root">
      {/* <style>{css}</style> */}

      {/* ── Range trigger ── */}
      <div ref={rangeRef} style={{ position: "relative" }}>
        <button
          className={`trp-btn-base trp-trigger${rangeOpen ? " open" : ""}`}
          onClick={() => { setRangeOpen((v) => !v); setRefreshOpen(false); }}
          title="Select time range"
        >
          <ClockIcon />
          <span className="trp-trigger-label">{triggerLabel}</span>
          <CaretIcon />
        </button>

        {rangeOpen && (
          <div className="trp-panel">
            {/* Quick ranges */}
            <div className="trp-quick">
              <div className="trp-col-header">Quick ranges</div>
              {QUICK_RANGES.map((r) => (
                <div
                  key={r.key}
                  className={`trp-quick-row${selectedQuick?.key === r.key ? " sel" : ""}`}
                  onClick={() => applyQuick(r)}
                >
                  {r.label}
                  {selectedQuick?.key === r.key && <span className="trp-check">✓</span>}
                </div>
              ))}
            </div>

            {/* Absolute */}
            <div className="trp-abs">
              <div className="trp-abs-header">Absolute time range</div>

              <div className="trp-field">
                <label className="trp-field-label">From</label>
                <input
                  type="datetime-local"
                  className="trp-input"
                  value={absFrom}
                  onChange={(e) => { setAbsFrom(e.target.value); setSelectedQuick(null); setAbsError(""); }}
                />
              </div>

              <div className="trp-field">
                <label className="trp-field-label">To</label>
                <input
                  type="datetime-local"
                  className="trp-input"
                  value={absTo}
                  onChange={(e) => { setAbsTo(e.target.value); setSelectedQuick(null); setAbsError(""); }}
                />
              </div>

              {absError
                ? <div className="trp-error">⚠ {absError}</div>
                : <div style={{ minHeight: 17 }} />
              }

              <button className="trp-apply" onClick={applyAbsolute}>
                Apply time range
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Refresh ── */}
      <div ref={refreshRef} style={{ position: "relative" }}>
        <button
          className={`trp-btn-base trp-refresh-btn${isRunning ? " running" : ""}`}
          onClick={() => { setRefreshOpen((v) => !v); setRangeOpen(false); }}
          title="Auto refresh"
        >
          <RefreshIcon spinning={spinning} />
          {isRunning ? `Auto refresh · ${refreshInterval.label}` : "Auto refresh"}
          {countdown !== null && <span className="trp-countdown">{countdown}s</span>}
          {isRunning && <span className="trp-running-dot" />}
        </button>

        {refreshOpen && (
          <div className="trp-refresh-panel">
            <div className="trp-col-header" style={{ padding: "10px 14px 6px" }}>Refresh interval</div>
            <div
              className="trp-refresh-row trp-refresh-now"
              onClick={() => { manualRefresh(); setRefreshOpen(false); }}
            >
              <RefreshIcon spinning={false} />
              Refresh now
            </div>
            {REFRESH_INTERVALS.map((ri) => (
              <div
                key={ri.label}
                className={`trp-refresh-row${refreshInterval.label === ri.label ? " sel" : ""}`}
                onClick={() => { setRefreshInterval(ri); setRefreshOpen(false); }}
              >
                {ri.label}
                {refreshInterval.label === ri.label && <span className="trp-check">✓</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeRangePicker;